// External
import _ from "lodash";
import PropTypes from "prop-types";
import React, { memo, useCallback, useMemo } from "react";

// Hooks
import { useI18n, useToggleState } from "Hooks";

// Components
import Accordian from "UIComponents/Accordian";

// Relative Components
import ListItem from "../ListItem";

// CSS
import classes from "./Accordion.scss";

const HEADER_STYLE = { padding: 0, cursor: "default", display: "block" };
const CONTENT_STYLE = {
  marginBottom: 0,
  borderTop: "1px solid var(--border-secondary)",
};

const Accordion = ({
  id,
  name,
  src,
  shape,
  options,
  selectedStudentIds,
  setSelectedStudentIds,
  testId,
}) => {
  const { t } = useI18n();
  const [isExpanded, handleToggle] = useToggleState(false);

  const isChecked = useMemo(() => {
    if (_.size(options) === 0) {
      return selectedStudentIds.has(id);
    }
    return _.every(options, option => selectedStudentIds.has(option.id));
  }, [id, options, selectedStudentIds]);

  const isIndeterminate = useMemo(
    () =>
      !isChecked &&
      _.some(options, option => selectedStudentIds.has(option.id)),
    [isChecked, options, selectedStudentIds]
  );

  const onOptionCheckboxClick = useCallback(
    ({ id }) => {
      setSelectedStudentIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return newSet;
      });
    },
    [setSelectedStudentIds]
  );

  const handleCheckboxClick = useCallback(
    ({ id, checked }) => {
      if (_.size(options) === 0) {
        onOptionCheckboxClick({ id, checked });
        return;
      }

      setSelectedStudentIds(prev => {
        const newSet = new Set(prev);
        const optionIds = _.map(options, "id");

        if (checked) {
          _.forEach(optionIds, id => newSet.add(id));
        } else {
          _.forEach(optionIds, id => newSet.delete(id));
        }

        return newSet;
      });
    },
    [onOptionCheckboxClick, options, setSelectedStudentIds]
  );

  const renderExpandedContent = useCallback(
    () =>
      _.map(options, ({ id: optionId, name: optionName, src: optionSrc }) => (
        <ListItem
          key={optionId}
          id={optionId}
          name={optionName}
          src={optionSrc}
          isChecked={selectedStudentIds.has(optionId)}
          onCheckboxClick={onOptionCheckboxClick}
          testId={`${testId}-${optionId}`}
        />
      )),
    [options, selectedStudentIds, onOptionCheckboxClick, testId]
  );

  if (_.size(options) === 0) {
    return (
      <ListItem
        id={id}
        name={name}
        src={src}
        isChecked={isChecked}
        onCheckboxClick={handleCheckboxClick}
        testId={`${testId}-${id}`}
      />
    );
  }

  return (
    <Accordian
      isExpanded={isExpanded}
      isDisabled
      showLabelBorder={false}
      hideContainerBorder
      headerStyle={HEADER_STYLE}
      mainContentContainerStyle={CONTENT_STYLE}
      mainContainerClass={classes.accordionContainer}
      testId={`${testId}-${id}`}
      customTitleComponent={
        <ListItem
          id={id}
          name={t("common:label_with_count_parenthesis", {
            label: name,
            count: _.size(options),
          })}
          src={src}
          shape={shape}
          isAccordion
          isExpanded={isExpanded}
          onClick={handleToggle}
          onCheckboxClick={handleCheckboxClick}
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
          testId={`${testId}-${id}`}
        />
      }
      getExpandedContent={renderExpandedContent}
    />
  );
};

Accordion.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  src: PropTypes.string,
  shape: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      src: PropTypes.string,
    })
  ).isRequired,
  selectedStudentIds: PropTypes.instanceOf(Set).isRequired,
  setSelectedStudentIds: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

Accordion.defaultProps = {
  src: null,
  shape: "circle",
  testId: "",
};

export default memo(Accordion);
